import type { GridCard } from '@Components/BigBrowse/Model/Classes/GridCard';
import { getCard, getGap } from '@Components/BigBrowse/Model/Classes/GridCard';
import type { PlaneSize } from '@Components/BigBrowse/Model/ModelTypes';
import { PlaneTypes } from '@Components/BigBrowse/Model/ModelTypes';
import type {
  MultiSectionFigmaLayout,
  SectionItemDM,
} from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/common/MultiSectionView.types';
import { get2ColumnsWidth } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/common/columnCalculator';
import type {
  ViewData,
  ViewDataWithSection,
} from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/common/multisection.data';
import { pushGapToColumn } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/common/sections.common';
import { addRestOfProducts } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/shelf/common/addRestOfProducts';
import { addShelfDisplaceGaps } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/shelf/common/addShelfDisplaceGaps';
import { distributeProducts } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/shelf/common/distributeProducts';
import { getMarginUnderHero } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/shelf/common/getMarginUnderHero';
import { getProductEquivalentHeight } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/shelf/common/productCards';
import { isEven, isOdd } from '@Utils/miscUtils';
import { pipeAsyncTransformer } from '@Utils/pipeAsync';
import pipeWith from 'ramda/src/pipeWith';

export const addSection = (
  sliceIndexes?: [number, number],
  shouldAddHeader = false
) =>
  pipeWith(pipeAsyncTransformer, [
    addSectionHero(sliceIndexes),
    addColumnHeroAndRowGaps(sliceIndexes),
    addShelfDisplaceGaps(isOdd),
    addFirstRowProducts(sliceIndexes),
    addRestOfProducts(shouldAddHeader),
    addShelfDisplaceGaps(isEven),
  ]) as (viewData: ViewDataWithSection) => Promise<ViewData>;

const addSectionHero =
  (productIndexes?: [number, number]) =>
  async (viewData: ViewDataWithSection): Promise<ViewDataWithSection> => {
    if (!productIndexes) {
      return viewData; // Don't add hero
    }

    const side = get2ColumnsWidth(viewData);

    const [start, end] = productIndexes;
    const heroIndex = start === 0 ? end : 0; // Hero index in on the right or left
    viewData.layoutTable![heroIndex].push(
      getSectionHeroCard(
        viewData,
        { width: side, height: side },
        viewData.layout
      ),
      getMarginUnderHero(viewData)
    );
    viewData.layoutTable![heroIndex + 1].push(
      getGap({ width: viewData.columnWidth!, height: side }),
      getMarginUnderHero(viewData)
    );

    return viewData;
  };

const addColumnHeroAndRowGaps =
  (productIndexes?: [number, number]) =>
  async (viewData: ViewDataWithSection): Promise<ViewDataWithSection> => {
    if (!productIndexes) {
      return viewData; // Don't add gaps
    }

    const [start, end] = productIndexes;

    // If no section products, add hero sized gap, otherwise, add product sized gap
    const gapSize = !viewData.section.products?.length
      ? get2ColumnsWidth(viewData)
      : getProductEquivalentHeight(viewData);

    viewData
      .layoutTable!.slice(start, end)
      .forEach(pushGapToColumn(gapSize, viewData));
    return viewData;
  };

export function addFirstRowProducts(productIndexes?: [number, number]) {
  return async (
    viewData: ViewDataWithSection
  ): Promise<ViewDataWithSection> => {
    if (!productIndexes) {
      return viewData; // Don't add products
    }

    const products = viewData.section.products.slice(
      0,
      viewData.layoutTable!.length - 2
    );

    const distributed = distributeProducts(viewData.section)({
      products,
      viewData,
      layout: viewData.layout,
      numOfColumns: viewData.layoutTable!.length - 2,
    });

    const [start, end] = productIndexes;
    viewData
      .layoutTable!.slice(start, end)
      .forEach((column) => column.push(...distributed.shift()!));
    return viewData;
  };
}

export function getSectionHeroCard(
  viewData: ViewData & {
    section: SectionItemDM;
  },
  size: PlaneSize,
  layout: MultiSectionFigmaLayout = 'full'
): GridCard<PlaneTypes.Card_MultiSection_SectionHero> {
  const card = getCard<PlaneTypes.Card_MultiSection_SectionHero>({
    type: PlaneTypes.Card_MultiSection_SectionHero,
    size,
    blockHover: true,
    data: {
      dataModel: viewData.modelData,
      sectionModel: viewData.section,
      id: viewData.section.id.toString(),
      key: [
        'BrandConcession',
        viewData.modelData.concessionId,
        'section',
        viewData.section.id,
        'hero',
      ].join('_'),
      imageUrl: viewData.section.heroImageUrl,
      type: undefined,
      layout,
      sectionId: viewData.section.id,
    },
  });

  card.key = card.data?.key;

  return card;
}
